import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { AttachmentV2 } from "src/app/backend/models/attachment/attachment";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";
import {
  AppointmentTemplate,
  AppointmentTemplateCreate,
  AppointmentTemplateLight,
  AppointmentTemplateUpdate
} from "../models/appointment-template/appointment-template";
import { searchListOfObjects } from "src/app/support/search";
import { isUndefined, omitBy } from "lodash";
import { alphabetical } from "radash";
import { omitUndefinedAndNullProperties } from "src/app/support/object";

@Injectable()
export class AppointmentTemplateBackend {
  constructor(private http: HttpClient) {}

  load(id: string): Observable<AppointmentTemplate> {
    return this.http.get<AppointmentTemplate>(
      `${environment.snoots.api.calendar}/templates/${id}`
    );
  }

  loadLight(id: string): Observable<AppointmentTemplateLight> {
    return this.http.get<AppointmentTemplate>(
      `${environment.snoots.api.calendar}/templates/${id}`,
      {
        params: {
          light: true
        }
      }
    );
  }

  list(
    request: PageRequest,
    tenant?: string
  ): Observable<PaginatedResult<AppointmentTemplate>> {
    // @ts-ignore [expect-error]: 🐛 There is a null/undefined issue here which could cause bugs! Please fix me.
    return this.http.get<PaginatedResult<AppointmentTemplate>>(
      `${environment.snoots.api.calendar}/templates`,
      {
        // @ts-ignore [expect-error]: 🐛 There is a null/undefined issue here which could cause bugs! Please fix me.
        params: omitBy(
          {
            p: request.page,
            s: request.pageSize,
            tenant,
            ...request.filterValues
          },
          isUndefined
        )
      }
    );
  }

  listAllLight(request: {
    internal: boolean | undefined;
    tenant?: string;
  }): Observable<AppointmentTemplateLight[]> {
    const { tenant, ...rest } = request;
    return this.list(
      {
        page: 1,
        pageSize: 10000,
        filterValues: omitUndefinedAndNullProperties({ light: true, ...rest })
      },
      tenant
    ).pipe(map((response) => alphabetical(response.docs, (x) => x.summary)));
  }

  search(
    request: PageRequest,
    name: string
  ): Observable<PaginatedResult<AppointmentTemplate>> {
    // @ts-ignore [expect-error]: 🐛 There is a null/undefined issue here which could cause bugs! Please fix me.
    return this.http.get<PaginatedResult<AppointmentTemplate>>(
      `${environment.snoots.api.calendar}/templates`,
      {
        params: {
          p: request.page,
          s: request.pageSize,
          name
        }
      }
    );
  }

  searchLight(
    query: string,
    internal: boolean | undefined,
    tenant?: string
  ): Observable<AppointmentTemplateLight[]> {
    return this.listAllLight({ internal, tenant }).pipe(
      map((items) =>
        alphabetical(
          searchListOfObjects(items, (item) => item.summary, query),
          (x) => x.summary
        )
      )
    );
  }

  loadAttachments(id: string): Observable<AttachmentV2[]> {
    return this.http
      .get<AttachmentV2[]>(
        `${environment.snoots.api.calendar}/templates/${id}/attachments`
      )
      .pipe(
        map((attachments) => {
          return attachments.map((x) => ({
            ...x,
            extension: x.name?.endsWith(".pdf") ? "pdf" : undefined
          }));
        })
      );
  }

  addAttachment(
    id: string,
    name: string,
    storageId: string,
    type: string
  ): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.calendar}/templates/${id}/attachments`,
      {
        name,
        storageId,
        type
      }
    );
  }

  deleteAttachment(templateId: string, attachmentId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.calendar}/attachments/${attachmentId}`
    );
  }

  @UserNotify({
    successMessage: marker("Modello appuntamento creato"),
    errorMessage: marker("Impossibile creare il modello"),
    progressIndeterminate: true,
    progressMessage: marker("Crezione modello appuntamento")
  })
  create(payload: AppointmentTemplateCreate): Observable<AppointmentTemplate> {
    return this.http.post<AppointmentTemplate>(
      `${environment.snoots.api.calendar}/templates`,
      payload
    );
  }

  @UserNotify({
    successMessage: marker("Modello appuntamento aggiornato"),
    errorMessage: marker("Impossibile aggiornare il modello")
  })
  update(
    id: string,
    payload: Partial<AppointmentTemplateUpdate>
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.calendar}/templates/${id}`,
      payload
    );
  }

  @UserNotify({
    successMessage: marker("Modello appuntamento eliminato"),
    errorMessage: marker("Impossibile eliminare il modello")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.calendar}/templates/${id}`
    );
  }
}
